import { useLayoutEffect, useRef, type TextareaHTMLAttributes } from 'react';

/** Paragraph input with natural wrapping and a bounded six-line editing area. */
export function AutoGrowTextarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const resize = () => {
      const css = getComputedStyle(element);
      const line = Number.parseFloat(css.lineHeight) || 24;
      const padding =
        (Number.parseFloat(css.paddingTop) || 0) + (Number.parseFloat(css.paddingBottom) || 0);
      const border =
        (Number.parseFloat(css.borderTopWidth) || 0) +
        (Number.parseFloat(css.borderBottomWidth) || 0);
      element.style.height = 'auto';
      const max = 6 * line + padding + border;
      const needed = element.scrollHeight + border;
      element.style.height = `${Math.min(max, Math.max(3 * line + padding + border, needed))}px`;
      element.style.overflowY = needed > max ? 'auto' : 'hidden';
    };
    resize();
    let width = element.parentElement?.clientWidth;
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver((entries) => {
            const next = entries[0]?.contentRect.width;
            if (next !== width) {
              width = next;
              resize();
            }
          });
    if (element.parentElement) observer?.observe(element.parentElement);
    window.addEventListener('resize', resize);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, [props.value]);
  return <textarea {...props} rows={3} ref={ref} style={{ ...props.style, resize: 'none' }} />;
}
